import React, { useState, useEffect, useRef } from 'react';
import { createPortal } from 'react-dom';
import { motion } from 'framer-motion';
import {
  Users,
  Calendar,
  Filter,
  Download,
  ChevronDown,
  ChevronUp,
  Search,
  X,
  CheckCircle,
  XCircle,
  Clock,
  Sword,
  Shield,
  Scroll,
  Gem,
  ScrollText,
  Award,
  User
} from 'lucide-react';
import Card from '../../components/ui/Card';
import Button from '../../components/ui/Button';
import Input from '../../components/ui/Input';
import { LoadingSpinner } from '../../components/ui/Loading';
import api from '../../services/api';
import { adminService } from '../../services';
import {
  getEventParticipationStatusText,
  getEventStatusText,
  getEventTypeTranslation
} from '../../utils/eventTypeTranslation';
import {CLASSES, getCharacterStatsText, getClassTypeTranslation} from '../../utils/characterTranslation';
import { formatDate, formatRelativeTime } from '../../utils/dateUtils';

// Componente de Dropdown com Portal
const DropdownPortal = ({ isOpen, buttonRef, children, onClose }) => {
  const dropdownRef = useRef(null);
  const [position, setPosition] = useState({ top: 0, left: 0, width: 0 });

  // Função para calcular posição
  const updatePosition = () => {
    if (buttonRef.current) {
      const rect = buttonRef.current.getBoundingClientRect();
      setPosition({
        top: rect.bottom + 4,
        left: rect.left,
        width: rect.width
      });
    }
  };

  // Atualizar posição quando abrir
  useEffect(() => {
    if (isOpen) {
      updatePosition();
    }
  }, [isOpen, buttonRef]);

  // Gerenciar eventos
  useEffect(() => {
    if (!isOpen) return;

    const handleClickOutside = (event) => {
      if (
        dropdownRef.current && 
        !dropdownRef.current.contains(event.target) &&
        buttonRef.current &&
        !buttonRef.current.contains(event.target)
      ) {
        onClose();
      }
    };

    const handleScroll = () => {
      updatePosition();
    };

    const handleResize = () => {
      updatePosition();
    };

    // Adicionar listeners com delay
    const timeoutId = setTimeout(() => {
      document.addEventListener('mousedown', handleClickOutside);
      window.addEventListener('scroll', handleScroll, true);
      window.addEventListener('resize', handleResize);
    }, 100);

    return () => {
      clearTimeout(timeoutId);
      document.removeEventListener('mousedown', handleClickOutside);
      window.removeEventListener('scroll', handleScroll, true);
      window.removeEventListener('resize', handleResize);
    };
  }, [isOpen, onClose, buttonRef]);

  if (!isOpen) return null;

  return createPortal(
    <div
      ref={dropdownRef}
      className="bg-slate-800 border border-white/10 rounded-lg shadow-2xl overflow-y-auto"
      style={{
        position: 'fixed',
        top: `${position.top}px`,
        left: `${position.left}px`,
        width: `${position.width}px`,
        maxHeight: '300px',
        zIndex: 99999
      }}
    >
      {children}
    </div>,
    document.body
  );
};

const AdminReports = () => {
  const [activeReport, setActiveReport] = useState('members'); // 'members', 'events' ou 'quests'
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  // Funções de tradução
  const translateStatus = (status) => {
    const translations = {
      'ACTIVE': 'Ativo',
      'INACTIVE': 'Inativo',
      'SCHEDULED': 'Agendado',
      'COMPLETED': 'Completado',
      'CANCELLED': 'Cancelado',
      'CONFIRMED': 'Confirmado',
      'DECLINED': 'Recusado',
      'PENDING': 'Pendente',
      'APPROVED': 'Aprovado',
      'REJECTED': 'Rejeitado'
    };
    return translations[status] || status;
  };

  // Estados para relatório de membros
  const [members, setMembers] = useState([]);
  const [memberFilters, setMemberFilters] = useState({
    search: '',
    status: 'ACTIVE',
    selectedClasses: [], // Array de classes selecionadas
    minLevel: '',
    maxLevel: '',
    selectedRelics: [], // Array de IDs de relíquias selecionadas
    hasItems: '',
    hasQuests: ''
  });
  const [expandedMember, setExpandedMember] = useState(null);
  const [availableRelics, setAvailableRelics] = useState([]); // Relíquias disponíveis do backend
  const [showClassDropdown, setShowClassDropdown] = useState(false);
  const [showRelicDropdown, setShowRelicDropdown] = useState(false);
  const classButtonRef = useRef(null);
  const relicButtonRef = useRef(null);

  // Estados para relatório de eventos
  const [events, setEvents] = useState([]);
  const [eventFilters, setEventFilters] = useState({
    search: '',
    status: '',
    participationStatus: '' // CONFIRMED, DECLINED, PENDING
  });
  const [expandedEvent, setExpandedEvent] = useState(null);

  // Estados para relatório de quests
  const [completedQuests, setCompletedQuests] = useState([]);
  const [questStats, setQuestStats] = useState({});
  const [byCharacter, setByCharacter] = useState([]);
  const [allCharacters, setAllCharacters] = useState([]);
  const [questFilters, setQuestFilters] = useState({
    characterId: '',
    questId: '',
    startDate: '',
    endDate: ''
  });
  const [questSearchTerm, setQuestSearchTerm] = useState('');

  // Carregar dados ao mudar de relatório
  useEffect(() => {
    if (activeReport === 'members') {
      loadMembers();
      loadAvailableRelics();
    } else if (activeReport === 'events') {
      loadEvents();
    } else if (activeReport === 'quests') {
      loadQuests();
    }
  }, [activeReport]);

  useEffect(() => {
    if (activeReport === 'quests') {
      loadQuests();
    }
  }, [questFilters]);



  const loadAvailableRelics = async () => {
    try {
      const response = await api.get('/admin/relics', {
        params: { limit: 9999 } // Buscar todas as relíquias
      });
      setAvailableRelics(response.data.data?.relics || []);
    } catch (err) {
      console.error('Erro ao carregar relíquias:', err);
    }
  };

  const loadMembers = async () => {
    try {
      setLoading(true);
      setError('');

      // Buscar personagens com relacionamentos
      const response = await api.get('/characters', {
        params: { limit: 9999 }
      });

      const characters = response.data.characters || [];
      
      // Buscar detalhes de itens, relíquias e quests para cada personagem
      const charactersWithDetails = await Promise.all(
        characters.map(async (char) => {
          try {
            // Processar itens (pode vir como items[].item ou items[] direto)
            const items = (char.items || []).map(i => i.item ? ({ level: i.enhancement, ...i.item }) : i);
            
            // Processar relíquias (pode vir como relics[].relic ou relics[] direto)
            const relics = (char.relics || []).map(r => r.relic || r);
            
            // Processar quests (pode vir como quests[].quest ou quests[] direto)
            const quests = (char.quests || []).map(q => q.quest || q);

            return {
              ...char,
              itemsCount: items.length,
              relicsCount: relics.length,
              questsCount: quests.length,
              items: items,
              relics: relics,
              quests: quests
            };
          } catch (err) {
            console.error(`  ❌ Erro ao buscar detalhes de ${char.name}:`, err);
            return {
              ...char,
              itemsCount: 0,
              relicsCount: 0,
              questsCount: 0,
              items: [],
              relics: [],
              quests: []
            };
          }
        })
      );

      setMembers(charactersWithDetails);
    } catch (err) {
      console.error('Erro ao carregar membros:', err);
      setError('Erro ao carregar relatório de membros');
    } finally {
      setLoading(false);
    }
  };

  const loadEvents = async () => {
    try {
      setLoading(true);
      setError('');

      const response = await api.get('/admin/events/all');
      const eventsData = response.data.data || [];

      setEvents(eventsData);
    } catch (err) {
      console.error('Erro ao carregar eventos:', err);
      setError('Erro ao carregar relatório de eventos');
    } finally {
      setLoading(false);
    }
  };

  const loadQuests = async () => {
    try {
      setLoading(true);
      setError('');

      const [reportData, charactersData] = await Promise.all([
        adminService.getQuestsReport(questFilters),
        api.get('/characters', { params: { limit: 1000, status: '' } })
      ]);

      setCompletedQuests(reportData.completedQuests || []);
      setQuestStats(reportData.stats || {});
      setByCharacter(reportData.byCharacter || []);
      setAllCharacters(charactersData.data?.characters || []);
    } catch (err) {
      console.error('Erro ao carregar relatório de quests:', err);
      setError('Erro ao carregar relatório de quests');
    } finally {
      setLoading(false);
    }
  };

  // Filtrar membros
  const filteredMembers = members.filter(member => {
    // Filtro de busca
    if (memberFilters.search) {
      const search = memberFilters.search.toLowerCase();
      const matchName = member.name?.toLowerCase().includes(search);
      const matchUser = member.user?.name?.toLowerCase().includes(search);
      if (!matchName && !matchUser) return false;
    }

    // Filtro de status
    if (memberFilters.status && member.status !== memberFilters.status) {
      return false;
    }

    // Filtro de classes (personagem deve estar em uma das selecionadas)
    if (memberFilters.selectedClasses && memberFilters.selectedClasses.length > 0) {
      if (!memberFilters.selectedClasses.includes(member.class)) {
        return false;
      }
    }

    // Filtro de nível mínimo
    if (memberFilters.minLevel && member.level < parseInt(memberFilters.minLevel)) {
      return false;
    }

    // Filtro de nível máximo
    if (memberFilters.maxLevel && member.level > parseInt(memberFilters.maxLevel)) {
      return false;
    }

    // Filtro de relíquias específicas (personagem deve ter TODAS as selecionadas)
    if (memberFilters.selectedRelics && memberFilters.selectedRelics.length > 0) {
      const memberRelicIds = (member.relics || []).map(r => r.id);
      const hasAllSelectedRelics = memberFilters.selectedRelics.every(selectedRelicId => 
        memberRelicIds.includes(selectedRelicId)
      );
      if (!hasAllSelectedRelics) {
        return false;
      }
    }

    // Filtro de itens
    if (memberFilters.hasItems === 'yes' && member.itemsCount === 0) {
      return false;
    }
    if (memberFilters.hasItems === 'no' && member.itemsCount > 0) {
      return false;
    }

    // Filtro de quests
    if (memberFilters.hasQuests === 'yes' && member.questsCount === 0) {
      return false;
    }
    if (memberFilters.hasQuests === 'no' && member.questsCount > 0) {
      return false;
    }

    return true;
  });

  // Filtrar eventos
  const filteredEvents = events.filter(event => {
    // Filtro de busca
    if (eventFilters.search) {
      const search = eventFilters.search.toLowerCase();
      if (!event.name?.toLowerCase().includes(search)) return false;
    }

    // Filtro de status
    if (eventFilters.status && event.status !== eventFilters.status) {
      return false;
    }

    return true;
  });

  // Filtrar participações por status
  const getFilteredParticipations = (participations) => {
    if (!eventFilters.participationStatus) return participations;
    return participations.filter(p => p.status === eventFilters.participationStatus);
  };

  const clearMemberFilters = () => {
    setMemberFilters({
      search: '',
      status: 'ACTIVE',
      class: '',
      minLevel: '',
      maxLevel: '',
      selectedClasses: [],
      selectedRelics: [],
      hasItems: '',
      hasQuests: ''
    });
  };

  const clearEventFilters = () => {
    setEventFilters({
      search: '',
      status: '',
      participationStatus: ''
    });
  };

  const exportToCSV = (data, filename) => {
    if (data.length === 0) return;
    
    // Pega os headers (nomes das colunas) do primeiro objeto
    const headers = Object.keys(data[0]);
    
    // Cria a linha de header
    const headerRow = headers.join(',');
    
    // Cria as linhas de dados
    const dataRows = data.map(row => Object.values(row).join(','));
    
    // Junta header + dados
    const csv = [headerRow, ...dataRows].join('\n');
    
    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = filename;
    a.click();
  };

  const exportMembersCSV = () => {
    // Primeiro, coletar todas as relíquias únicas em ordem alfabética
    const allRelics = new Set();
    filteredMembers.forEach(m => {
      if (m.relics && m.relics.length > 0) {
        m.relics.forEach(relic => {
          const relicName = relic.name || relic.relicName || 'Relíquia';
          allRelics.add(relicName);
        });
      }
    });
    const sortedRelics = Array.from(allRelics).sort();

    const data = filteredMembers.map(m => {
      const itensFormatted = [];
      // Lista de itens com níveis
      const itemsList = m.items && m.items.length > 0
        ? m.items.map(item => {
            const itemName = item.name || item.itemName || 'Item';
            const itemLevel = item.level ;
            const itemType = item.type;
            itensFormatted[itemType] = { itemName, itemLevel };
            return { itemType: { itemName,itemLevel } };
          }) : [];

      // Criar mapa de relíquias do personagem
      const memberRelics = new Set();
      if (m.relics && m.relics.length > 0) {
        m.relics.forEach(relic => {
          const relicName = relic.name || relic.relicName || 'Relíquia';
          memberRelics.add(relicName);
        });
      }

      // Criar objeto com colunas base
      const row = {
        'Personagem': m.name,
        'Usuario': m.user?.name || 'N/A',
        'Data_Criacao': m.createdAt ? new Date(m.createdAt).toLocaleDateString('pt-BR') : 'N/A',
        'Classe': getClassTypeTranslation(m.class),
        'Nivel': m.level,
        'Pontos': m.totalPoints || 0,
        'Roupao/Armadura': itensFormatted['ARMOR']?.itemName || 'N/A',
        'Level Roupao/Armadura': itensFormatted['ARMOR']?.itemLevel || 'N/A',
        'Arma': itensFormatted['WEAPON']?.itemName || 'N/A',
        'Level_Arma': itensFormatted['WEAPON']?.itemLevel || 'N/A',
        'Escudo': itensFormatted['SHIELD']?.itemName || 'N/A',
        'Level_Escudo': itensFormatted['SHIELD']?.itemLevel || 'N/A',
        'Luvas': itensFormatted['GLOVES']?.itemName || 'N/A',
        'Level_Luvas': itensFormatted['GLOVES']?.itemLevel || 'N/A',
        'Bracelete': itensFormatted['BRACELET']?.itemName || 'N/A',
        'Level_Bracelete': itensFormatted['BRACELET']?.itemLevel || 'N/A',
        'Botas': itensFormatted['BOOTS']?.itemName || 'N/A',
        'Level_Botas': itensFormatted['BOOTS']?.itemLevel || 'N/A',
      };

      // Adicionar uma coluna para cada relíquia
      sortedRelics.forEach(relicName => {
        row[`Reliquia_${relicName}`] = memberRelics.has(relicName) ? 'Sim' : 'Não';
      });

      // Adicionar colunas finais
      row['Total_Reliquias'] = m.relicsCount;
      row['Quests'] = m.questsCount;
      row['Status'] = getCharacterStatsText(m.status);

      return row;
    });
    exportToCSV(data, 'relatorio-membros.csv');
  };

  const exportEventsCSV = () => {
    const data = filteredEvents.flatMap(event => 
      (event.participations || []).map(p => ({
        'Evento': event.name,
        'Data': new Date(event.startDate).toLocaleDateString('pt-BR'),
        'Tipo': getEventTypeTranslation(event.type),
        'Status Evento': getEventStatusText(event.status),
        'Participante': p.character?.name || 'N/A',
        'Usurio': p.user?.name || 'N/A',
        'Status_Participacao': getEventParticipationStatusText(p.status),
        'Piloto': p.pilot ? p.pilot : ''
      }))
    );
    exportToCSV(data, 'relatorio-eventos.csv');
  };

  const exportQuestsCSV = () => {
    const filteredByChar = byCharacter.filter(item => {
      if (!questSearchTerm) return true;
      const character = item.character;
      return character?.name?.toLowerCase().includes(questSearchTerm.toLowerCase()) ||
             character?.user?.name?.toLowerCase().includes(questSearchTerm.toLowerCase());
    });

    // Coletar todas as quests únicas em ordem alfabética
    const allQuests = new Set();
    filteredByChar.forEach(item => {
      if (item.quests && item.quests.length > 0) {
        item.quests.forEach(cq => {
          const questName = cq.quest?.name || 'Quest';
          allQuests.add(questName);
        });
      }
    });
    const sortedQuests = Array.from(allQuests).sort();

    // Criar uma linha por personagem
    const data = filteredByChar.map(item => {
      // Criar mapa de quests do personagem
      const characterQuests = new Map();
      if (item.quests && item.quests.length > 0) {
        item.quests.forEach(cq => {
          const questName = cq.quest?.name || 'Quest';
          const completedDate = cq.completedAt 
            ? new Date(cq.completedAt).toLocaleDateString('pt-BR')
            : 'Sim';
          characterQuests.set(questName, completedDate);
        });
      }

      // Criar objeto com colunas base
      const row = {
        'Personagem': item.character?.name || 'N/A',
        'Classe': getClassTypeTranslation(item.character?.class) || 'N/A',
        'Level': item.character?.level || 0,
        'Usuário': item.character?.user?.name || 'N/A',
        'Total_Quests': item.questsCompleted || 0
      };

      // Adicionar uma coluna para cada quest
      sortedQuests.forEach(questName => {
        row[`Quest_${questName}`] = characterQuests.has(questName) 
          ? characterQuests.get(questName) 
          : 'Não';
      });

      return row;
    });

    exportToCSV(data, 'relatorio-quests.csv');
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'CONFIRMED':
      case 'APPROVED':
      case 'ACTIVE':
      case 'POINTS_AWARDED':
        return 'text-green-400 bg-green-500/20 border-green-500/30';
      case 'DECLINED':
      case 'REJECTED':
        return 'text-red-400 bg-red-500/20 border-red-500/30';
      case 'PENDING':
      case 'SCHEDULED':
        return 'text-amber-400 bg-amber-500/20 border-amber-500/30';
      default:
        return 'text-gray-400 bg-gray-500/20 border-gray-500/30';
    }
  };

  const getStatusIcon = (status) => {
    switch (status) {
      case 'CONFIRMED':
      case 'APPROVED':
        return <CheckCircle className="w-4 h-4" />;
      case 'DECLINED':
      case 'REJECTED':
        return <XCircle className="w-4 h-4" />;
      case 'PENDING':
        return <Clock className="w-4 h-4" />;
      default:
        return null;
    }
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div>
        <h1 className="text-4xl font-black text-transparent bg-clip-text bg-gradient-to-r from-cyan-400 via-blue-500 to-purple-600 mb-2">
          📊 Relatórios Administrativos
        </h1>
        <p className="text-gray-400">
          Análises detalhadas de membros e eventos do clã
        </p>
      </div>

      {/* Seletor de Relatório */}
      <div className="flex gap-4">
        <Button
          variant={activeReport === 'members' ? 'primary' : 'secondary'}
          size="lg"
          onClick={() => setActiveReport('members')}
          className="flex-1"
        >
          <Users className="w-5 h-5 mr-2" />
          Relatório de Membros
        </Button>
        <Button
          variant={activeReport === 'events' ? 'primary' : 'secondary'}
          size="lg"
          onClick={() => setActiveReport('events')}
          className="flex-1"
        >
          <Calendar className="w-5 h-5 mr-2" />
          Relatório de Eventos
        </Button>
        <Button
          variant={activeReport === 'quests' ? 'primary' : 'secondary'}
          size="lg"
          onClick={() => setActiveReport('quests')}
          className="flex-1"
        >
          <ScrollText className="w-5 h-5 mr-2" />
          Relatório de Quests
        </Button>
      </div>

      {/* Relatório de Membros */}
      {activeReport === 'members' && (
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="space-y-6"
        >
          {/* Filtros de Membros */}
          <Card variant="glass" padding="lg">
            <div className="flex items-center justify-between mb-4">
              <div className="flex items-center gap-2">
                <Filter className="w-5 h-5 text-cyan-400" />
                <h3 className="text-xl font-bold text-white">Filtros</h3>
              </div>
              <Button variant="ghost" size="sm" onClick={clearMemberFilters}>
                <X className="w-4 h-4 mr-1" />
                Limpar
              </Button>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
              {/* Busca */}
              <div className="lg:col-span-1">
                <Input
                  placeholder="Buscar por nome..."
                  value={memberFilters.search}
                  onChange={(e) => setMemberFilters({ ...memberFilters, search: e.target.value })}
                  icon={Search}
                />
              </div>

              {/* Ativo */}
              <select
                value={memberFilters.status}
                onChange={(e) => setMemberFilters({ ...memberFilters, class: e.target.value })}
                className="px-4 py-2 bg-white/5 border border-white/10 rounded-lg text-white focus:outline-none focus:ring-2 focus:ring-cyan-500/50"
              >
                <option value="" className="bg-slate-800">Todas status</option>
                <option value="ACTIVE" className="bg-slate-800">Ativos</option>
                <option value="INACTIVE" className="bg-slate-800">Inativos</option>
              </select>

              {/* Classes (multi-select com checkboxes) */}
              <div className="relative">
                <button
                  ref={classButtonRef}
                  type="button"
                  onClick={() => setShowClassDropdown(prev => !prev)}
                  className="w-full px-4 py-2 bg-white/5 border border-white/10 rounded-lg text-white focus:outline-none focus:ring-2 focus:ring-cyan-500/50 text-left flex items-center justify-between"
                >
                  <span className={memberFilters.selectedClasses.length === 0 ? 'text-gray-400' : ''}>
                    {memberFilters.selectedClasses.length === 0 
                      ? 'Todas as Classes' 
                      : `${memberFilters.selectedClasses.length} classe(s)`}
                  </span>
                  <ChevronDown className="w-4 h-4" />
                </button>
                
                <DropdownPortal
                  isOpen={showClassDropdown}
                  buttonRef={classButtonRef}
                  onClose={() => setShowClassDropdown(false)}
                >
                  {CLASSES.map(classType => (
                      <label
                        key={classType}
                        className="flex items-center gap-2 px-4 py-2 hover:bg-white/5 cursor-pointer transition-colors"
                      >
                        <input
                          type="checkbox"
                          checked={memberFilters.selectedClasses.includes(classType)}
                          onChange={(e) => {
                            if (e.target.checked) {
                              setMemberFilters({
                                ...memberFilters,
                                selectedClasses: [...memberFilters.selectedClasses, classType]
                              });
                            } else {
                              setMemberFilters({
                                ...memberFilters,
                                selectedClasses: memberFilters.selectedClasses.filter(c => c !== classType)
                              });
                            }
                          }}
                          className="w-4 h-4 rounded border-white/10 bg-white/5 text-cyan-500 focus:ring-cyan-500/50"
                        />
                        <span className="text-white">{getClassTypeTranslation(classType)}</span>
                      </label>
                  ))}
                </DropdownPortal>
              </div>

              {/* Nível Mínimo */}
              <Input
                type="number"
                placeholder="Nível mínimo"
                value={memberFilters.minLevel}
                onChange={(e) => setMemberFilters({ ...memberFilters, minLevel: e.target.value })}
              />

              {/* Nível Máximo */}
              <Input
                type="number"
                placeholder="Nível máximo"
                value={memberFilters.maxLevel}
                onChange={(e) => setMemberFilters({ ...memberFilters, maxLevel: e.target.value })}
              />

              {/* Relíquias (multi-select com checkboxes) */}
              <div className="relative">
                <button
                  ref={relicButtonRef}
                  type="button"
                  onClick={() => setShowRelicDropdown(prev => !prev)}
                  className="w-full px-4 py-2 bg-white/5 border border-white/10 rounded-lg text-white focus:outline-none focus:ring-2 focus:ring-amber-500/50 text-left flex items-center justify-between"
                >
                  <span className={memberFilters.selectedRelics.length === 0 ? 'text-gray-400' : ''}>
                    {memberFilters.selectedRelics.length === 0 
                      ? 'Todas as Relíquias' 
                      : `${memberFilters.selectedRelics.length} relíquia(s)`}
                  </span>
                  <ChevronDown className="w-4 h-4" />
                </button>
                
                <DropdownPortal
                  isOpen={showRelicDropdown}
                  buttonRef={relicButtonRef}
                  onClose={() => setShowRelicDropdown(false)}
                >
                  {availableRelics.length === 0 ? (
                      <div className="px-4 py-2 text-gray-400 text-sm">Carregando relíquias...</div>
                    ) : (
                      availableRelics.sort((a, b) => a.name.localeCompare(b.name)).map(relic => (
                        <label
                          key={relic.id}
                          className="flex items-center gap-2 px-4 py-2 hover:bg-white/5 cursor-pointer transition-colors"
                        >
                          <input
                            type="checkbox"
                            checked={memberFilters.selectedRelics.includes(relic.id)}
                            onChange={(e) => {
                              if (e.target.checked) {
                                setMemberFilters({
                                  ...memberFilters,
                                  selectedRelics: [...memberFilters.selectedRelics, relic.id]
                                });
                              } else {
                                setMemberFilters({
                                  ...memberFilters,
                                  selectedRelics: memberFilters.selectedRelics.filter(id => id !== relic.id)
                                });
                              }
                            }}
                            className="w-4 h-4 rounded border-white/10 bg-white/5 text-cyan-500 focus:ring-cyan-500/50"
                          />
                          <span className="text-white">{relic.name}</span>
                        </label>
                      ))
                    )}
                </DropdownPortal>
              </div>

              {/* Tem Itens */}
              <select
                value={memberFilters.hasItems}
                onChange={(e) => setMemberFilters({ ...memberFilters, hasItems: e.target.value })}
                className="px-4 py-2 bg-white/5 border border-white/10 rounded-lg text-white focus:outline-none focus:ring-2 focus:ring-cyan-500/50"
              >
                <option value="" className="bg-slate-800">Itens: Todos</option>
                <option value="yes" className="bg-slate-800">Com Itens</option>
                <option value="no" className="bg-slate-800">Sem Itens</option>
              </select>

              {/* Tem Quests */}
              <select
                value={memberFilters.hasQuests}
                onChange={(e) => setMemberFilters({ ...memberFilters, hasQuests: e.target.value })}
                className="px-4 py-2 bg-white/5 border border-white/10 rounded-lg text-white focus:outline-none focus:ring-2 focus:ring-cyan-500/50"
              >
                <option value="" className="bg-slate-800">Quests: Todas</option>
                <option value="yes" className="bg-slate-800">Com Quests</option>
                <option value="no" className="bg-slate-800">Sem Quests</option>
              </select>
            </div>

            {/* Tags de classes e relíquias selecionadas */}
            {(memberFilters.selectedClasses.length > 0 || memberFilters.selectedRelics.length > 0) && (
              <div className="mt-4 pt-4 border-t border-white/10 space-y-3">
                {/* Classes selecionadas */}
                {memberFilters.selectedClasses.length > 0 && (
                  <div>
                    <div className="text-sm text-gray-400 mb-2">
                      Filtrando por classes:
                    </div>
                    <div className="flex flex-wrap gap-2">
                      {memberFilters.selectedClasses.map(classType => (
                        <span
                          key={classType}
                          className="inline-flex items-center gap-2 px-3 py-1 bg-cyan-500/20 border border-cyan-500/30 rounded-lg text-cyan-400 text-sm"
                        >
                          {getClassTypeTranslation(classType)}
                          <button
                            onClick={() => {
                              setMemberFilters({
                                ...memberFilters,
                                selectedClasses: memberFilters.selectedClasses.filter(c => c !== classType)
                              });
                            }}
                            className="hover:text-cyan-300 transition-colors"
                          >
                            ×
                          </button>
                        </span>
                      ))}
                      <button
                        onClick={() => setMemberFilters({ ...memberFilters, selectedClasses: [] })}
                        className="text-xs text-gray-400 hover:text-white transition-colors underline"
                      >
                        Limpar classes
                      </button>
                    </div>
                  </div>
                )}

                {/* Relíquias selecionadas */}
                {memberFilters.selectedRelics.length > 0 && (
                  <div>
                    <div className="text-sm text-gray-400 mb-2">
                      Filtrando por relíquias:
                    </div>
                    <div className="flex flex-wrap gap-2">
                      {memberFilters.selectedRelics.map(relicId => {
                        const relic = availableRelics.find(r => r.id === relicId);
                        return relic ? (
                          <span
                            key={relicId}
                            className="inline-flex items-center gap-2 px-3 py-1 bg-amber-500/20 border border-amber-500/30 rounded-lg text-amber-400 text-sm"
                          >
                            {relic.name}
                            <button
                              onClick={() => {
                                setMemberFilters({
                                  ...memberFilters,
                                  selectedRelics: memberFilters.selectedRelics.filter(id => id !== relicId)
                                });
                              }}
                              className="hover:text-amber-300 transition-colors"
                            >
                              ×
                            </button>
                          </span>
                        ) : null;
                      })}
                      <button
                        onClick={() => setMemberFilters({ ...memberFilters, selectedRelics: [] })}
                        className="text-xs text-gray-400 hover:text-white transition-colors underline"
                      >
                        Limpar relíquias
                      </button>
                    </div>
                  </div>
                )}
              </div>
            )}
          </Card>

          {/* Ações e Estatísticas */}
          <div className="flex items-center justify-between">
            <div className="text-gray-300">
              Mostrando <span className="font-bold text-cyan-400">{filteredMembers.length}</span> de <span className="font-bold text-white">{members.length}</span> membros
            </div>
            <Button variant="primary" onClick={exportMembersCSV}>
              <Download className="w-4 h-4 mr-2" />
              Exportar CSV
            </Button>
          </div>

          {/* Lista de Membros */}
          {loading ? (
            <div className="flex justify-center py-12">
              <LoadingSpinner />
            </div>
          ) : error ? (
            <Card variant="glass" padding="lg">
              <p className="text-red-400 text-center">{error}</p>
            </Card>
          ) : filteredMembers.length === 0 ? (
            <Card variant="glass" padding="lg">
              <p className="text-gray-400 text-center py-8">Nenhum membro encontrado com os filtros aplicados</p>
            </Card>
          ) : (
            <div className="space-y-3">
              {filteredMembers.map((member) => (
                <Card key={member.id} variant="glass" padding="default">
                  <div
                    className="cursor-pointer"
                    onClick={() => setExpandedMember(expandedMember === member.id ? null : member.id)}
                  >
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-4 flex-1">
                        {/* Avatar/Classe */}
                        <div className="w-12 h-12 bg-gradient-to-br from-cyan-500 to-blue-600 rounded-xl flex items-center justify-center text-white font-bold">
                          {member.class?.charAt(0)}
                        </div>

                        {/* Info */}
                        <div className="flex-1">
                          <div className="flex items-center gap-2 mb-1">
                            <h3 className="font-bold text-white">{member.name}</h3>
                            <span className="text-sm text-gray-400">• {member.user?.name || 'N/A'}</span>
                          </div>
                          <div className="flex items-center gap-4 text-sm text-gray-400">
                            <span className="text-cyan-400 font-semibold">{getClassTypeTranslation(member.class)}</span>
                            <span>Nível {member.level}</span>
                            <span>{member.totalPoints || 0} pontos</span>
                          </div>
                        </div>

                        {/* Stats Rápidas */}
                        <div className="hidden lg:flex items-center gap-4 text-sm">
                          <div className="flex items-center gap-1 text-purple-400">
                            <Shield className="w-4 h-4" />
                            <span>{member.itemsCount}</span>
                          </div>
                          <div className="flex items-center gap-1 text-amber-400">
                            <Gem className="w-4 h-4" />
                            <span>{member.relicsCount}</span>
                          </div>
                          <div className="flex items-center gap-1 text-blue-400">
                            <Scroll className="w-4 h-4" />
                            <span>{member.questsCount}</span>
                          </div>
                        </div>

                        {/* Expand Icon */}
                        <div className="text-gray-400">
                          {expandedMember === member.id ? (
                            <ChevronUp className="w-5 h-5" />
                          ) : (
                            <ChevronDown className="w-5 h-5" />
                          )}
                        </div>
                      </div>
                    </div>

                    {/* Detalhes Expandidos */}
                    {expandedMember === member.id && (
                      <motion.div
                        initial={{ opacity: 0, height: 0 }}
                        animate={{ opacity: 1, height: 'auto' }}
                        className="mt-4 pt-4 border-t border-white/10"
                      >
                        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                          {/* Itens */}
                          <div>
                            <h4 className="text-sm font-semibold text-purple-400 mb-2 flex items-center gap-1">
                              <Shield className="w-4 h-4" />
                              Itens ({member.itemsCount})
                            </h4>
                            {member.items && member.items.length > 0 ? (
                              <ul className="space-y-1 text-sm text-gray-300">
                                {member.items.map((item, idx) => (
                                  <li key={idx} className="flex justify-between items-center">
                                    <span className="truncate flex-1">• {item.name || item.itemName}</span>
                                    {item.level && (
                                      <span className="text-cyan-400 font-semibold ml-2">+{item.level}</span>
                                    )}
                                  </li>
                                ))}
                              </ul>
                            ) : (
                              <p className="text-sm text-gray-500">Nenhum item</p>
                            )}
                          </div>

                          {/* Relíquias */}
                          <div>
                            <h4 className="text-sm font-semibold text-amber-400 mb-2 flex items-center gap-1">
                              <Gem className="w-4 h-4" />
                              Relíquias ({member.relicsCount})
                            </h4>
                            {member.relics && member.relics.length > 0 ? (
                              <ul className="space-y-1 text-sm text-gray-300">
                                {member.relics.map((relic, idx) => (
                                  <li key={idx} className="truncate">• {relic.name || relic.relicName}</li>
                                ))}
                              </ul>
                            ) : (
                              <p className="text-sm text-gray-500">Nenhuma relíquia</p>
                            )}
                          </div>

                          {/* Quests */}
                          <div>
                            <h4 className="text-sm font-semibold text-blue-400 mb-2 flex items-center gap-1">
                              <Scroll className="w-4 h-4" />
                              Quests ({member.questsCount})
                            </h4>
                            {member.quests && member.quests.length > 0 ? (
                              <ul className="space-y-1 text-sm text-gray-300">
                                {member.quests.map((quest, idx) => (
                                  <li key={idx} className="truncate">• {quest.name || quest.questName}</li>
                                ))}
                              </ul>
                            ) : (
                              <p className="text-sm text-gray-500">Nenhuma quest</p>
                            )}
                          </div>
                        </div>
                      </motion.div>
                    )}
                  </div>
                </Card>
              ))}
            </div>
          )}
        </motion.div>
      )}

      {/* Relatório de Eventos */}
      {activeReport === 'events' && (
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="space-y-6"
        >
          {/* Filtros de Eventos */}
          <Card variant="glass" padding="lg">
            <div className="flex items-center justify-between mb-4">
              <div className="flex items-center gap-2">
                <Filter className="w-5 h-5 text-cyan-400" />
                <h3 className="text-xl font-bold text-white">Filtros</h3>
              </div>
              <Button variant="ghost" size="sm" onClick={clearEventFilters}>
                <X className="w-4 h-4 mr-1" />
                Limpar
              </Button>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              {/* Busca */}
              <Input
                placeholder="Buscar por nome do evento..."
                value={eventFilters.search}
                onChange={(e) => setEventFilters({ ...eventFilters, search: e.target.value })}
                icon={Search}
              />

              {/* Status do Evento */}
              <select
                value={eventFilters.status}
                onChange={(e) => setEventFilters({ ...eventFilters, status: e.target.value })}
                className="px-4 py-2 bg-white/5 border border-white/10 rounded-lg text-white focus:outline-none focus:ring-2 focus:ring-cyan-500/50"
              >
                <option value="" className="bg-slate-800">Status: Todos</option>
                <option value="SCHEDULED" className="bg-slate-800">{translateStatus('SCHEDULED')}</option>
                <option value="ACTIVE" className="bg-slate-800">{translateStatus('ACTIVE')}</option>
                <option value="COMPLETED" className="bg-slate-800">{translateStatus('COMPLETED')}</option>
                <option value="CANCELLED" className="bg-slate-800">{translateStatus('CANCELLED')}</option>
              </select>

              {/* Status de Participação */}
              <select
                value={eventFilters.participationStatus}
                onChange={(e) => setEventFilters({ ...eventFilters, participationStatus: e.target.value })}
                className="px-4 py-2 bg-white/5 border border-white/10 rounded-lg text-white focus:outline-none focus:ring-2 focus:ring-cyan-500/50"
              >
                <option value="" className="bg-slate-800">Participação: Todas</option>
                <option value="CONFIRMED" className="bg-slate-800">{translateStatus('CONFIRMED')}</option>
                <option value="DECLINED" className="bg-slate-800">{translateStatus('DECLINED')}</option>
                <option value="PENDING" className="bg-slate-800">{translateStatus('PENDING')}</option>
              </select>
            </div>
          </Card>

          {/* Ações e Estatísticas */}
          <div className="flex items-center justify-between">
            <div className="text-gray-300">
              Mostrando <span className="font-bold text-cyan-400">{filteredEvents.length}</span> de <span className="font-bold text-white">{events.length}</span> eventos
            </div>
            <Button variant="primary" onClick={exportEventsCSV}>
              <Download className="w-4 h-4 mr-2" />
              Exportar CSV
            </Button>
          </div>

          {/* Lista de Eventos */}
          {loading ? (
            <div className="flex justify-center py-12">
              <LoadingSpinner />
            </div>
          ) : error ? (
            <Card variant="glass" padding="lg">
              <p className="text-red-400 text-center">{error}</p>
            </Card>
          ) : filteredEvents.length === 0 ? (
            <Card variant="glass" padding="lg">
              <p className="text-gray-400 text-center py-8">Nenhum evento encontrado com os filtros aplicados</p>
            </Card>
          ) : (
            <div className="space-y-3">
              {filteredEvents.map((event) => {
                const participations = event.participations || [];
                const filteredParts = getFilteredParticipations(participations);
                const confirmed = participations.filter(p => p.status === 'CONFIRMED' || p.status === 'POINTS_AWARDED' ).length;
                const declined = participations.filter(p => p.status === 'DECLINED').length;
                const pending = participations.filter(p => p.status !== 'CONFIRMED' && p.status !== 'DECLINED' && p.status !== 'POINTS_AWARDED').length;

                return (
                  <Card key={event.id} variant="glass" padding="default">
                    <div
                      className="cursor-pointer"
                      onClick={() => setExpandedEvent(expandedEvent === event.id ? null : event.id)}
                    >
                      <div className="flex items-center justify-between">
                        <div className="flex items-center gap-4 flex-1">
                          {/* Icon */}
                          <div className="w-12 h-12 bg-gradient-to-br from-purple-500 to-pink-600 rounded-xl flex items-center justify-center text-white">
                            <Calendar className="w-6 h-6" />
                          </div>

                          {/* Info */}
                          <div className="flex-1">
                            <div className="flex items-center gap-2 mb-1">
                              <h3 className="font-bold text-white">{event.name}</h3>
                              <span className={`text-xs px-2 py-1 rounded-full border ${getStatusColor(event.status)}`}>
                                {getEventStatusText(event.status)}
                              </span>
                            </div>
                            <div className="flex items-center gap-4 text-sm text-gray-400">
                              <span>{new Date(event.startDate).toLocaleDateString('pt-BR')}</span>
                              <span>•</span>
                              <span>{getEventTypeTranslation(event.type)}</span>
                            </div>
                          </div>

                          {/* Stats Rápidas */}
                          <div className="hidden lg:flex items-center gap-6 text-sm">
                            <div className="flex items-center gap-1 text-green-400">
                              <CheckCircle className="w-4 h-4" />
                              <span>{confirmed}</span>
                            </div>
                            <div className="flex items-center gap-1 text-red-400">
                              <XCircle className="w-4 h-4" />
                              <span>{declined}</span>
                            </div>
                            <div className="flex items-center gap-1 text-amber-400">
                              <Clock className="w-4 h-4" />
                              <span>{pending}</span>
                            </div>
                          </div>

                          {/* Expand Icon */}
                          <div className="text-gray-400">
                            {expandedEvent === event.id ? (
                              <ChevronUp className="w-5 h-5" />
                            ) : (
                              <ChevronDown className="w-5 h-5" />
                            )}
                          </div>
                        </div>
                      </div>

                      {/* Detalhes Expandidos */}
                      {expandedEvent === event.id && (
                        <motion.div
                          initial={{ opacity: 0, height: 0 }}
                          animate={{ opacity: 1, height: 'auto' }}
                          className="mt-4 pt-4 border-t border-white/10"
                        >
                          {/* Estatísticas */}
                          <div className="grid grid-cols-3 gap-4 mb-4">
                            <div className="text-center p-3 bg-green-500/10 border border-green-500/30 rounded-lg">
                              <div className="text-2xl font-bold text-green-400">{confirmed}</div>
                              <div className="text-xs text-gray-400">Confirmados</div>
                            </div>
                            <div className="text-center p-3 bg-red-500/10 border border-red-500/30 rounded-lg">
                              <div className="text-2xl font-bold text-red-400">{declined}</div>
                              <div className="text-xs text-gray-400">Recusados</div>
                            </div>
                            <div className="text-center p-3 bg-amber-500/10 border border-amber-500/30 rounded-lg">
                              <div className="text-2xl font-bold text-amber-400">{pending}</div>
                              <div className="text-xs text-gray-400">Pendentes</div>
                            </div>
                          </div>

                          {/* Lista de Participações */}
                          {filteredParts.length > 0 ? (
                            <div className="space-y-2">
                              <h4 className="text-sm font-semibold text-gray-400 mb-2">
                                Participantes ({filteredParts.length})
                              </h4>
                              {filteredParts.map((participation, idx) => (
                                <div
                                  key={idx}
                                  className="flex items-center justify-between p-2 bg-white/5 rounded-lg"
                                >
                                  <div className="flex items-center gap-3">
                                    <div className="w-8 h-8 bg-gradient-to-br from-cyan-500 to-blue-600 rounded-lg flex items-center justify-center text-white text-xs font-bold">
                                      {participation.character?.name?.charAt(0) || '?'}
                                    </div>
                                    <div>
                                      <div className="text-sm font-semibold text-white">
                                        {participation.character?.name || 'N/A'}
                                      </div>
                                      <div className="text-xs text-gray-400">
                                        {participation.user?.name || 'N/A'}
                                      </div>
                                    </div>
                                  </div>
                                  <div className="flex items-center gap-2">
                                    {participation.pilot && (
                                      <span className="text-xs px-2 py-1 bg-blue-500/20 text-blue-400 rounded border border-blue-500/30">
                                        {participation.pilot}
                                      </span>
                                    )}
                                    <span className={`text-xs px-2 py-1 rounded border flex items-center gap-1 ${getStatusColor(participation.status)}`}>
                                      {getStatusIcon(participation.status)}
                                      {getEventParticipationStatusText(participation.status)}
                                    </span>
                                  </div>
                                </div>
                              ))}
                            </div>
                          ) : (
                            <p className="text-sm text-gray-500 text-center py-4">
                              Nenhuma participação com os filtros aplicados
                            </p>
                          )}
                        </motion.div>
                      )}
                    </div>
                  </Card>
                );
              })}
            </div>
          )}
        </motion.div>
      )}

      {/* Relatório de Quests */}
      {activeReport === 'quests' && (
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="space-y-6"
        >
          {/* Stats Cards */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            <Card variant="glass" className="relative overflow-hidden">
              <div className="absolute inset-0 bg-gradient-to-br from-cyan-500/10 to-transparent"></div>
              <div className="relative p-6 text-center">
                <CheckCircle className="w-8 h-8 text-cyan-400 mx-auto mb-2" />
                <p className="text-sm text-gray-400 mb-1">Total Completadas</p>
                <h3 className="text-3xl font-bold text-white">{questStats.totalCompleted || 0}</h3>
              </div>
            </Card>

            <Card variant="glass" className="relative overflow-hidden">
              <div className="absolute inset-0 bg-gradient-to-br from-purple-500/10 to-transparent"></div>
              <div className="relative p-6 text-center">
                <Users className="w-8 h-8 text-purple-400 mx-auto mb-2" />
                <p className="text-sm text-gray-400 mb-1">Personagens Ativos</p>
                <h3 className="text-3xl font-bold text-white">{questStats.uniqueCharacters || 0}</h3>
              </div>
            </Card>

            <Card variant="glass" className="relative overflow-hidden">
              <div className="absolute inset-0 bg-gradient-to-br from-amber-500/10 to-transparent"></div>
              <div className="relative p-6 text-center">
                <ScrollText className="w-8 h-8 text-amber-400 mx-auto mb-2" />
                <p className="text-sm text-gray-400 mb-1">Quests Únicas</p>
                <h3 className="text-3xl font-bold text-white">{questStats.uniqueQuests || 0}</h3>
              </div>
            </Card>
          </div>

          {/* Filtros */}
          <Card variant="glass" padding="lg">
            <div className="flex items-center justify-between mb-4">
              <div className="flex items-center gap-2">
                <Filter className="w-5 h-5 text-cyan-400" />
                <h3 className="text-xl font-bold text-white">Filtros</h3>
              </div>
              <Button 
                variant="ghost" 
                size="sm" 
                onClick={() => {
                  setQuestFilters({ characterId: '', questId: '', startDate: '', endDate: '' });
                  setQuestSearchTerm('');
                }}
              >
                <X className="w-4 h-4 mr-1" />
                Limpar
              </Button>
            </div>

            <div className="mt-4">
              <div className="relative">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
                <input
                  type="text"
                  placeholder="Buscar por nome do personagem ou usuário..."
                  value={questSearchTerm}
                  onChange={(e) => setQuestSearchTerm(e.target.value)}
                  className="w-full bg-white/5 border border-white/10 text-white rounded-xl pl-10 pr-4 py-2.5 focus:outline-none focus:ring-2 focus:ring-cyan-500/50 transition-all"
                />
              </div>
            </div>
          </Card>

          {/* Ações e Estatísticas */}
          <div className="flex items-center justify-between">
            <div className="text-gray-300">
              Mostrando <span className="font-bold text-cyan-400">{byCharacter.filter(item => {
                if (!questSearchTerm) return true;
                const character = item.character;
                return character?.name?.toLowerCase().includes(questSearchTerm.toLowerCase()) ||
                       character?.user?.name?.toLowerCase().includes(questSearchTerm.toLowerCase());
              }).length}</span> de <span className="font-bold text-white">{byCharacter.length}</span> personagens
            </div>
            <Button variant="primary" onClick={exportQuestsCSV}>
              <Download className="w-4 h-4 mr-2" />
              Exportar CSV
            </Button>
          </div>

          {/* Quests por Personagem */}
          {loading ? (
            <div className="flex justify-center py-12">
              <LoadingSpinner size="lg" />
            </div>
          ) : byCharacter.filter(item => {
            if (!questSearchTerm) return true;
            const character = item.character;
            return character?.name?.toLowerCase().includes(questSearchTerm.toLowerCase()) ||
                   character?.user?.name?.toLowerCase().includes(questSearchTerm.toLowerCase());
          }).length === 0 ? (
            <Card variant="glass">
              <div className="p-12 text-center">
                <ScrollText className="w-16 h-16 text-gray-600 mx-auto mb-4" />
                <p className="text-gray-400">Nenhuma quest completada encontrada</p>
              </div>
            </Card>
          ) : (
            <div className="space-y-6">
              {byCharacter.filter(item => {
                if (!questSearchTerm) return true;
                const character = item.character;
                return character?.name?.toLowerCase().includes(questSearchTerm.toLowerCase()) ||
                       character?.user?.name?.toLowerCase().includes(questSearchTerm.toLowerCase());
              }).map((item, index) => (
                <motion.div
                  key={item.character?.id || index}
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.3, delay: index * 0.05 }}
                >
                  <Card variant="glass" padding="lg">
                    <div className="flex items-center justify-between mb-4">
                      <div className="flex items-center gap-4">
                        <div className="w-12 h-12 bg-gradient-to-br from-cyan-500 to-blue-600 rounded-xl flex items-center justify-center">
                          <User className="w-6 h-6 text-white" />
                        </div>
                        <div>
                          <h3 className="text-lg font-semibold text-white">{item.character?.name}</h3>
                          <p className="text-sm text-gray-400">
                            {getClassTypeTranslation(item.character?.class)} - Level {item.character?.level}
                          </p>
                          {item.character?.user && (
                            <p className="text-xs text-cyan-400 mt-1">
                              Jogador: {item.character.user.name}
                            </p>
                          )}
                        </div>
                      </div>
                      <div className="text-right">
                        <div className="flex items-center gap-2 text-amber-400">
                          <Award className="w-5 h-5" />
                          <span className="text-2xl font-bold">{item.questsCompleted}</span>
                        </div>
                        <p className="text-xs text-gray-400 mt-1">Quests Completadas</p>
                      </div>
                    </div>

                    <div className="space-y-3">
                      {item.quests?.map((cq, idx) => (
                        <div
                          key={cq.id || idx}
                          className="bg-white/5 border border-white/10 rounded-lg p-4"
                        >
                          <div className="flex items-start justify-between gap-4">
                            <div className="flex-1">
                              <h4 className="font-medium text-white mb-1">{cq.quest?.name}</h4>
                              <p className="text-sm text-gray-400 mb-2">{cq.quest?.description}</p>
                              <div className="flex flex-wrap gap-2">
                                {cq.quest?.points && (
                                  <span className="px-2 py-1 rounded-lg text-xs bg-amber-500/20 text-amber-400 border border-amber-500/30">
                                    +{cq.quest.points} pontos
                                  </span>
                                )}
                              </div>
                            </div>
                            <div className="text-right text-sm">
                              <div className="flex items-center gap-2 text-gray-400 mb-1">
                                <Clock className="w-4 h-4" />
                                <span>{formatRelativeTime(cq.completedAt)}</span>
                              </div>
                              <p className="text-xs text-gray-500">{formatDate(cq.completedAt)}</p>
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                  </Card>
                </motion.div>
              ))}
            </div>
          )}
        </motion.div>
      )}
    </div>
  );
};

export default AdminReports;
